import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchPublicSettings } from '../api/openingHoursAPI';
import { useStorefront } from './StoreContext.js';
import { useTranslation } from 'react-i18next';
import { formatMoney, parseCurrencyFromSettings } from '../utils/currencyFormat';
import StoreLoader from '../components/UI/StoreLoader/StoreLoader.js';
import { pickStoreName } from '../lib/storefront/storeName.js';

const PublicSettingsContext = createContext(null);

const SETTINGS_CACHE_PREFIX = 'aaa_storefront_public_settings_restaurant:';

function readCachedSettings(storeId) {
    if (!storeId) return null;
    try {
        const raw = localStorage.getItem(`${SETTINGS_CACHE_PREFIX}${storeId}`);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
}

function writeCachedSettings(storeId, data) {
    if (!storeId || !data) return;
    try {
        localStorage.setItem(`${SETTINGS_CACHE_PREFIX}${storeId}`, JSON.stringify(data));
    } catch {
        /* private mode / blocked storage / quota */
    }
}

export function PublicSettingsProvider({ children }) {
    const { store, organization, ready } = useStorefront();
    const storeId = store?.id || null;
    // Last saved copy for this store — lets repeat visits render the merchant's data immediately.
    const cachedSettings = useMemo(() => readCachedSettings(storeId), [storeId]);
    const [fetched, setFetched] = useState({ storeId: null, data: null, settled: false });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const isCurrentStore = fetched.storeId === storeId;
    const publicSettings = (isCurrentStore && fetched.data) || cachedSettings;
    const settled = isCurrentStore && fetched.settled;

    const fetchSettings = useCallback(async (isBackground = false) => {
        if (!storeId) return;

        try {
            if (!isBackground) {
                setLoading(true);
            }
            setError(null);
            const data = await fetchPublicSettings(storeId);
            setFetched({ storeId, data, settled: true });
            writeCachedSettings(storeId, data);
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Error fetching public settings:', err);
            setError(err);
            // Keep whatever we already had; just mark the first load as finished so the page renders.
            setFetched((prev) => ({
                storeId,
                data: prev.storeId === storeId ? prev.data : null,
                settled: true,
            }));
        } finally {
            if (!isBackground) {
                setLoading(false);
            }
        }
    }, [storeId]);

    useEffect(() => {
        if (!ready || !storeId) return;

        fetchSettings();
        const interval = setInterval(() => {
            fetchSettings(true);
        }, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [ready, storeId, fetchSettings]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                fetchSettings(true);
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [fetchSettings]);

    const { i18n } = useTranslation();
    const visitorLanguage = i18n.language;
    const { currencyCode, currencySymbol, storeLocale, formatPrice } = useMemo(() => {
        const { code, symbol, locale: settingsLocale } = parseCurrencyFromSettings(publicSettings);
        // Prices follow the visitor's language (Arabic visitor: "12.50 ر.س", English: "SAR 12.50");
        // the store's own locale is the fallback.
        const locale = visitorLanguage || settingsLocale;
        const moneyOpts = { fallbackSymbol: symbol, locale };
        return {
            currencyCode: code,
            currencySymbol: symbol,
            storeLocale: locale,
            formatPrice: (amount) => formatMoney(amount, code, moneyOpts)
        };
    }, [publicSettings, visitorLanguage]);

    const value = useMemo(
        () => ({
            publicSettings,
            loading,
            error,
            lastUpdated,
            refetch: () => fetchSettings(false),
            currencyCode,
            currencySymbol,
            storeLocale,
            formatPrice
        }),
        [publicSettings, loading, error, lastUpdated, fetchSettings, currencyCode, currencySymbol, storeLocale, formatPrice]
    );

    // First visit (nothing cached yet): hold the page until the merchant's settings arrive,
    // so the built-in logo/name/address and default page toggles never flash.
    if (storeId && !publicSettings && !settled) {
        return (
            <StoreLoader name={pickStoreName({ organization, store })} />
        );
    }

    return <PublicSettingsContext.Provider value={value}>{children}</PublicSettingsContext.Provider>;
}

export function usePublicSettings() {
    const ctx = useContext(PublicSettingsContext);
    if (!ctx) {
        throw new Error('usePublicSettings must be used within PublicSettingsProvider');
    }
    return ctx;
}
